import { useEffect, useRef, useState } from 'react'
import { api } from 'src/utils/api'

const cacheKey = 'amazonChatAvailability'
const interval = 5 * 60 * 1000 // Every 5 minutes, a request will be made to check chat availability.

const useAmazonChatAvailability = () => {
  const [chatAvailability, setChatAvailability] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const getAmazonChatAvailability = async () => {
    setLoading(true)
    setError(null)

    try {
      const catchedAvailability = sessionStorage.getItem(cacheKey)

      if (catchedAvailability) {
        const { displayChat, timestamp } = JSON.parse(catchedAvailability)

        if (Date.now() - timestamp < interval) {
          setChatAvailability(displayChat)
          return
        }
      }
      const data = await api.getAmazonChatAvailability()
      setChatAvailability(data.displayChat)
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ displayChat: data.displayChat, timestamp: Date.now() })
      )
    } catch (e: any) {
      setError(e.message)
      console.log(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAmazonChatAvailability()
    if (!intervalRef.current) {
      intervalRef.current = setInterval(getAmazonChatAvailability, interval)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !intervalRef.current) {
        getAmazonChatAvailability()
        intervalRef.current = setInterval(getAmazonChatAvailability, interval)
      } else if (
        document.visibilityState !== 'visible' &&
        intervalRef.current
      ) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    if (document.visibilityState === 'visible' && !intervalRef.current) {
      intervalRef.current = setInterval(getAmazonChatAvailability, interval)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  return { chatAvailability, loading, error }
}

export default useAmazonChatAvailability
